import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '@app/reducers';
import { ACCEPTABLE_ALL_MAJOR_FILE_EXTENSIONS, addFormControls, ConfirmDialogModel, ConfirmDialogPopupComponent, formConfigs, IApplicationResponse, isValidFilename, LoggedInUserModel, ModulesBasedApiSuffix, prepareRequiredHttpParams, removeFormControls, ResponseMessageTypes, setRequiredValidator, SnackbarService } from '@app/shared';
import { CrudService, HttpCancelService, RxjsService } from '@app/shared/services';
import { CollectionModuleApiSuffixModels } from '@modules/collection';
import { BalanceOfContractDocumentCaptureModel } from '@modules/customer';
import { loggedInUserData } from '@modules/others';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin } from 'rxjs';

declare let $;
@Component({
    selector: 'app-balance-of-contract-document-capture',
    templateUrl: './balance-of-contract-document-capture.component.html',
    styleUrls: ['./balance-of-contract.component.scss']
})
export class BalanceOfContractDocumentCaptureComponent implements OnInit {
    documentUploadForm: FormGroup;
    loggedInUserData: LoggedInUserModel;
    isFormSubmittedSuccessfully = false;
    message = 'Are you sure you want to submit this request for DOA Approval?';
    payload;
    documentationTypes = [];
    balanceOfContractDocuments = [];
    formConfigs = formConfigs;
    formData = new FormData();
    @ViewChild('selectField', { static: false }) selectField;
    @ViewChild('inputField', { static: false }) inputFileUploadField;
    @ViewChild('textAreaField', { static: false }) textAreaField;
    uploadedBlobFiles: File[] = [];
    uploadedFile: File;
    isApprovalRequest: boolean;
    isRaiseBoc: boolean;
    accept: any = ACCEPTABLE_ALL_MAJOR_FILE_EXTENSIONS;

    constructor(private formBuilder: FormBuilder, private dialog: MatDialog,
        private rxjsService: RxjsService,
        private crudService: CrudService,
        private httpCancelService: HttpCancelService,
        private router: Router,
        private route: ActivatedRoute,
        private snackbarService: SnackbarService,
        private store: Store<AppState>) {
        this.isRaiseBoc = this.route?.snapshot?.queryParams?.isRaiseBoc;
    }

    ngOnInit(): void {
        this.getForkJoinRequests();
        this.combineLatestNgrxStoreData();
        this.createUploadForm();
    }

    getForkJoinRequests(): void {
        forkJoin([
            this.crudService.get(ModulesBasedApiSuffix.COLLECTIONS, CollectionModuleApiSuffixModels.UX_DOCUMENT_TYPE_FOR_FILE_UPLOAD, null, false,
                prepareRequiredHttpParams({
                    enumName: 'BALANCEOFCONTRACT'
                }))])
            .subscribe((response: IApplicationResponse[]) => {
                response.forEach((respObj: IApplicationResponse, ix: number) => {
                    if (respObj.isSuccess && respObj.statusCode == 200) {
                        switch (ix) {
                            case 0:
                                this.documentationTypes = respObj.resources;
                                break;
                        }
                    }
                });
                this.rxjsService.setGlobalLoaderProperty(false);
            });
    }

    showDialog() {
        this.rxjsService.setDialogOpenProperty(true);
    }

    closeDialog() {
        this.rxjsService.setDialogOpenProperty(false);
        this.documentUploadForm = addFormControls(this.documentUploadForm, [{ controlName: 'documentConfigId', defaultValue: '' }, 'documentName']);
        this.documentUploadForm = setRequiredValidator(this.documentUploadForm, ['documentConfigId', 'documentName']);
    }

    combineLatestNgrxStoreData() {
        combineLatest([
            this.store.select(loggedInUserData),
            this.rxjsService.getAnyPropertyValue()]
        ).subscribe((response) => {
            this.loggedInUserData = new LoggedInUserModel(response[0]);

            this.payload = response[1];
            if (this.payload) {
                this.payload.balanceOfContractCharges.forEach(data => {
                    if (data?.amount == null) {
                        data.amount = 0
                    }
                })
            }
            console.log('payload', this.payload);
        });
    }

    createUploadForm() {
        let balanceOfContractDocumentCaptureModel = new BalanceOfContractDocumentCaptureModel();
        this.documentUploadForm = this.formBuilder.group({});
        Object.keys(balanceOfContractDocumentCaptureModel).forEach((key) => {
            this.documentUploadForm.addControl(key, new FormControl(balanceOfContractDocumentCaptureModel[key]));
        });
        this.documentUploadForm = setRequiredValidator(this.documentUploadForm, ['notes']);
    }

    uploadFile(event) {
        let uploadedFile = event.target.files[0];
        this.uploadedFile = event.target.files[0];
        const supportedExtensions = ['.doc', '.docx', '.jpg', '.jpeg', '.pdf', '.png', '.xls', '.xlsx', '.DOC', '.DOCX', '.JPG', '.JPEG', '.PDF', '.PNG', '.XLS', '.XLSX'];
        const path = uploadedFile?.name?.split('.');
        const extension = "." + path[path.length - 1];
        const fileNameCondition = isValidFilename(uploadedFile?.name, { supportedExtensions: supportedExtensions, extension: extension })?.errorMsg;
        if (fileNameCondition) {
            return this.snackbarService.openSnackbar(fileNameCondition);
        }
        this.documentUploadForm.get('documentName').setValue(uploadedFile['name']);
    }

    addDocument() {
        this.documentUploadForm = setRequiredValidator(this.documentUploadForm, ['documentConfigId', 'documentName']);
        if (this.documentUploadForm.invalid) {
            this.selectField.nativeElement.focus();
            this.selectField.nativeElement.blur();
            this.inputFileUploadField.nativeElement.focus();
            this.inputFileUploadField.nativeElement.blur();
            this.textAreaField.nativeElement.focus();
            this.textAreaField.nativeElement.blur();
            return;
        }
        let isDuplicatedRecord = false;
        for (let obj of this.balanceOfContractDocuments) {
            if (this.documentUploadForm.value.documentName == obj.documentName) {
                isDuplicatedRecord = true;
                break;
            }
        }
        if (isDuplicatedRecord) {
            this.snackbarService.openSnackbar('The uploaded file already exists..!!', ResponseMessageTypes.WARNING);
            this.documentUploadForm.get('documentName').setValue('');
        }
        else {
            this.uploadedBlobFiles.push(this.uploadedFile);
            let balanceOfContractDocumentObj = JSON.parse(JSON.stringify(this.documentUploadForm.value));
            delete balanceOfContractDocumentObj.notes;
            this.balanceOfContractDocuments.push(balanceOfContractDocumentObj);
            this.documentUploadForm.reset(new BalanceOfContractDocumentCaptureModel({ notes: this.documentUploadForm.value.notes }));
        }
    }

    removeDocument(index: number) {
        const message = `Are you sure you want to delete this?`;
        const dialogData = new ConfirmDialogModel("Confirm Action", message);
        const dialogRef = this.dialog.open(ConfirmDialogPopupComponent, {
            maxWidth: "400px",
            data: dialogData,
            disableClose: true
        });
        dialogRef.afterClosed().subscribe(dialogResult => {
            if (!dialogResult) return;
            this.balanceOfContractDocuments.splice(index, 1);
            this.uploadedBlobFiles.splice(index, 1);
        });
    }

    onSubmit(): void {
        if (this.documentUploadForm.get('notes').invalid) {
            return;
        }
        this.documentUploadForm = removeFormControls(this.documentUploadForm, ['documentConfigId', 'documentName']);
        this.isApprovalRequest = true;
    }

    onSubmitAfterConfirmation() {
        this.formData = new FormData();
        this.payload['balanceOfContractDocuments'] = this.balanceOfContractDocuments;
        this.payload['createdUserId'] = this.loggedInUserData.userId;
        this.payload['notes'] = this.documentUploadForm.get('notes').value;
        this.formData.append('payload', JSON.stringify(this.payload));
        this.uploadedBlobFiles.forEach((file) => {
            this.formData.append('file', file);
        });
        this.httpCancelService.cancelPendingRequestsOnFormSubmission();
        this.crudService.create(ModulesBasedApiSuffix.COLLECTIONS,
            CollectionModuleApiSuffixModels.BALANCE_OF_CONTRACT, this.formData)
            .subscribe((response: IApplicationResponse) => {
                if (response.isSuccess && response.statusCode === 200) {
                    this.isFormSubmittedSuccessfully = true;
                    this.message = 'Request has been submitted for DOA Approval';
                }
                this.rxjsService.setPopupLoaderProperty(false);
            });
    }

    onAcceptedResponse() {
        this.isApprovalRequest = false;
        if (this.route?.snapshot?.queryParams?.isRaiseBoc == 'true') {
            this.navigateToProcessCancellation();
        } else {
            this.rxjsService.setViewCustomerData({
                customerId: this.payload.customerId,
                addressId: this.payload.addressId,
                customerTab: 6,
                monitoringTab: null,
            })
            this.rxjsService.navigateToViewCustomerPage();
        }
    }

    backToBalanceOfContractPage() {
        let queryParams = {
            customerId: this.payload.customerId,
            addressId: this.payload?.addressId || this.route?.snapshot?.queryParams?.addressId,
            contractId: this.payload?.contractId,
            serviceId: this.route?.snapshot?.queryParams?.serviceId,
            subscriptionIds: this.payload?.subscriptionIds || this.route?.snapshot?.queryParams?.subscriptionIds,
        }
        if (this.isRaiseBoc) {
            queryParams['ticketId'] = this.route?.snapshot?.queryParams?.ticketId;
            queryParams['serviceCancellationId'] = this.route?.snapshot?.queryParams?.serviceCancellationId;
            queryParams['isRaiseBoc'] = this.isRaiseBoc;
        }
        this.router.navigate(['/customer/manage-customers/balance-of-contract'], {
            queryParams: queryParams
        });
    }

    navigateToProcessCancellation() {
        this.router.navigate(['/customer/manage-customers/ticket/process-cancellation/view'], {
            queryParams: {
                id: this.payload.contractId,
                customerId: this.payload.customerId, addressId: this.payload?.addressId || this.route?.snapshot?.queryParams?.addressId,
                serviceId: this.route?.snapshot?.queryParams?.serviceId,
                ticketId: this.route?.snapshot?.queryParams?.ticketId,
                serviceCancellationId: this.route?.snapshot?.queryParams?.serviceCancellationId,
                subscriptionIds: this.route?.snapshot?.queryParams?.subscriptionIds,
            }
        });
    }

    navigateToViewCustomer() {
        this.rxjsService.setViewCustomerData({
            customerId: this.payload?.customerId,
            addressId: this.payload?.addressId || this.route?.snapshot?.queryParams?.addressId,
            customerTab: 6,
            monitoringTab: null,
        })
        this.rxjsService.navigateToViewCustomerPage();
    }
}
